import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Button,
} from "@mui/material";
import { get } from "../../helpers/aws_api_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import * as url from "../../helpers/url_helper";
import { summaryTheme as t } from "./styles";
import SummaryAccountDetailContent from "./SummaryAccountDetailContent";
import SummaryKpiCards from "../Dashboard/SummaryCard/SummaryKpiCards";
import SummarySeverityChart from "../Dashboard/SummaryCard/SummarySeverityChart";
import SummaryCostChart from "../Dashboard/SummaryCard/SummaryCostChart";
import SummaryFrameworkComplianceChart from "../Dashboard/SummaryCard/SummaryFrameworkComplianceChart";
import { useAccountSummaryData } from "./useAccountSummaryData";

const ACCOUNTS_PAGE_SIZE = 100;

const getAccountLabel = (account) => {
  const num = account?.account_number ?? account?.accountNumber ?? account?.id ?? "";
  const name = account?.name ?? account?.account_name ?? "";
  if (name && name !== num) return `${name} (${num})`;
  return num || "—";
};

const SummaryPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState(null);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [frameworkFilter, setFrameworkFilter] = useState("");
  const [showAllServices, setShowAllServices] = useState(false);

  // Summary for selected account (or org-wide when not selected)
  const {
    data: summaryData,
    loading: summaryLoading,
    error: summaryError,
  } = useAccountSummaryData(selectedAccountNumber);
  const overall = summaryData?.overall ?? null;
  const services = summaryData?.services ?? [];

  const servicesHeatmap = useMemo(() => {
    if (!Array.isArray(services) || services.length === 0) {
      return { rows: [], columns: [] };
    }
    const columns = [
      { id: "arns_total", label: "ARNs" },
      { id: "security_failed_checks_total", label: "Security findings" },
      { id: "compliance_failed_controls_total", label: "Control findings" },
      { id: "cost_unblended_mtd_total", label: "Cost (Last 30 Days) (USD)", optional: true },
      { id: "arns_cloud_waste", label: "Cloud Waste", optional: true },
    ];

    // Min/max per metric across all services (normalize per column).
    const stats = {};
    columns.forEach((col) => {
      stats[col.id] = { min: Infinity, max: -Infinity };
    });

    services.forEach((svc) => {
      columns.forEach((col) => {
        const raw = svc[col.id];
        if (raw == null || raw === "") return;
        const value = Number(raw);
        if (!Number.isFinite(value)) return;
        const s = stats[col.id];
        if (value < s.min) s.min = value;
        if (value > s.max) s.max = value;
      });
    });

    const riskColumnIds = [
      "security_failed_checks_total",
      "compliance_failed_controls_total",
      "cost_unblended_mtd_total",
      "arns_cloud_waste",
    ];

    const rows = services.map((svc) => {
      const normalized = {};
      columns.forEach((col) => {
        const raw = svc[col.id];
        const value = raw == null || raw === "" ? null : Number(raw);
        if (!Number.isFinite(value)) {
          normalized[col.id] = { value: null, norm: null };
          return;
        }

        const { min, max } = stats[col.id];
        const range = max - min;
        const norm = range > 0 ? (value - min) / range : 0;
        normalized[col.id] = { value, norm: Math.min(1, Math.max(0, norm)) };
      });

      // Composite "impact" used for sorting + subtle highlighting.
      const scoreParts = riskColumnIds
        .map((colId) => normalized[colId]?.norm)
        .filter((n) => n != null && Number.isFinite(n));
      const riskScore = scoreParts.length
        ? scoreParts.reduce((a, b) => a + b, 0) / scoreParts.length
        : 0;

      return {
        key: svc.service || svc.name || "—",
        service: svc.service || svc.name || "—",
        cells: normalized,
        riskScore,
      };
    });

    // Sort so the most impacted services are visible first.
    const sortedRows = [...rows].sort((a, b) => (b.riskScore ?? 0) - (a.riskScore ?? 0));
    return { rows: sortedRows, columns };
  }, [services]);

  const getHeatColor = (norm) => {
    if (norm == null) return "transparent";
    // Stronger, more heatmap-like contrast.
    const alpha = 0.06 + 0.94 * Math.min(1, Math.max(0, norm)); // 0 => light, 1 => deep
    return `rgba(79, 70, 229, ${alpha})`; // indigo
  };

  const visibleServiceColumns = servicesHeatmap.columns.filter((col) => {
    if (!col.optional) return true;
    return servicesHeatmap.rows.some((r) => r.cells[col.id]?.value != null);
  });

  const SERVICE_ROW_LIMIT = 5;
  const displayedServiceRows = showAllServices
    ? servicesHeatmap.rows
    : servicesHeatmap.rows.slice(0, SERVICE_ROW_LIMIT);

  const servicesGridTemplateColumns = `180px repeat(${visibleServiceColumns.length}, minmax(120px, 1fr))`;

  const fetchAccounts = useCallback(async () => {
    setAccountsLoading(true);
    setAccountsError(null);
    try {
      const response = await get(url.GET_ARN_VIEWS_ACCOUNTS, {
        params: { page: 1, page_size: ACCOUNTS_PAGE_SIZE },
      });
      const status = response?.status;
      if (status >= 200 && status < 300) {
        const items = response?.items ?? [];
        const list = Array.isArray(items) ? items : [];
        setAccounts(list);
        if (list.length > 0) {
          const first = list[0];
          const num = first?.account_number ?? first?.accountNumber ?? first?.id ?? "";
          setSelectedAccountNumber((prev) => (prev ? prev : num));
        }
      } else {
        setAccountsError(getApiErrorMessage(response) || "Failed to load accounts");
      }
    } catch (err) {
      const errData = err?.data ?? err?.response?.data;
      setAccountsError(getApiErrorMessage(errData) || err?.message || "Failed to load accounts");
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  return (
    <Box
      sx={{
        padding: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        mb: "1.5rem",
        backgroundColor: t.colors.background,
        minHeight: "100vh",
        fontFamily: t.fontFamily,
      }}
    >
      {/* Page title + account selector (applies to summary + details) */}
      <Box
        sx={{
          mb: 2.5,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: t.colors.text,
              fontSize: "1.25rem",
              letterSpacing: "-0.01em",
              mb: 0.25,
              lineHeight: 1.2,
              fontFamily: t.fontFamily,
            }}
          >
            ARN Insight
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: t.colors.textMuted,
              fontSize: "0.8125rem",
              fontWeight: 400,
              lineHeight: 1.5,
              fontFamily: t.fontFamily,
            }}
          >
            Organization summary and account-level CSPM &amp; FinOps view.
          </Typography>
        </Box>
        <Box sx={{ minWidth: { xs: "100%", md: 280 }, maxWidth: 360 }}>
          <FormControl
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                fontFamily: t.fontFamily,
                fontSize: "0.875rem",
                backgroundColor: t.colors.card,
                borderRadius: t.radius.md,
                "& fieldset": { borderColor: t.colors.border },
                "&:hover fieldset": { borderColor: t.colors.textMuted },
                "&.Mui-focused fieldset": { borderColor: t.colors.accent, borderWidth: "1px" },
              },
            }}
          >
            <InputLabel id="summary-account-label" sx={{ fontFamily: t.fontFamily, fontSize: "0.8125rem" }}>
              View account
            </InputLabel>
            <Select
              labelId="summary-account-label"
              id="summary-account-select"
              value={selectedAccountNumber || ""}
              label="View account"
              onChange={(e) => {
                const value = e.target.value;
                setSelectedAccountNumber(value);
                // Reset filters when switching accounts to avoid stale filters
                setSeverityFilter("");
                setFrameworkFilter("");
              }}
              sx={{ fontFamily: t.fontFamily, fontWeight: 500, "& .MuiSelect-select": { py: 1.25 } }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 1.5,
                    borderRadius: t.radius.md,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    maxHeight: 320,
                    "& .MuiMenuItem-root": { fontFamily: t.fontFamily, fontSize: "0.875rem" },
                  },
                },
              }}
            >
              {accountsLoading ? (
                <MenuItem value="" disabled>Loading…</MenuItem>
              ) : accountsError ? (
                <MenuItem value="" disabled>{accountsError}</MenuItem>
              ) : (
                accounts.map((acc, i) => {
                  const num = acc?.account_number ?? acc?.accountNumber ?? acc?.id ?? "";
                  if (!num) return null;
                  return (
                    <MenuItem key={num || i} value={num}>
                      {getAccountLabel(acc)}
                    </MenuItem>
                  );
                })
              )}
              {!accountsLoading && !accountsError && accounts.length === 0 && (
                <MenuItem value="" disabled>No accounts</MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Top half: KPI cards and charts */}
      <Box
        sx={{
          borderRadius: t.radius.lg,
          backgroundColor: t.colors.card,
          border: `1px solid ${t.colors.border}`,
          boxShadow: t.shadow,
          p: 2,
          mb: 3,
        }}
      >
        {summaryLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 4 }}>
            <CircularProgress size={24} sx={{ color: t.colors.accent }} />
            <Typography sx={{ color: t.colors.textMuted, fontSize: "0.875rem", fontFamily: t.fontFamily }}>Loading summary…</Typography>
          </Box>
        )}
        {summaryError && (
          <Typography sx={{ color: t.colors.destructive, fontSize: "0.875rem", fontFamily: t.fontFamily }}>{summaryError}</Typography>
        )}
        {!summaryLoading && !summaryError && (
          <>
            <Box sx={{ mb: 2 }}>
              <SummaryKpiCards overall={overall} />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 2,
              }}
            >
              <SummarySeverityChart
                securityFailedBySeverity={overall?.security_failed_checks_by_severity_total}
                onSeverityClick={(sev) =>
                  setSeverityFilter((prev) => (prev === sev ? "" : sev))
                }
              />
              <SummaryFrameworkComplianceChart
                overall={overall}
                framework={frameworkFilter}
                onFrameworkChange={(fw) =>
                  setFrameworkFilter((prev) => (prev === fw ? "" : fw))
                }
              />
              <SummaryCostChart overall={overall} services={services} />
            </Box>

            {servicesHeatmap.rows.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.5,
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: t.colors.text,
                      fontFamily: t.fontFamily,
                    }}
                  >
                    Service impact heatmap
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setShowAllServices((v) => !v)}
                      sx={{
                        fontFamily: t.fontFamily,
                        textTransform: "none",
                        fontSize: "0.75rem",
                        borderColor: t.colors.border,
                        color: t.colors.textMuted,
                        px: 1.25,
                        "&:hover": { borderColor: t.colors.borderLight, backgroundColor: t.colors.background },
                      }}
                    >
                      {showAllServices ? `Show top ${SERVICE_ROW_LIMIT}` : "Show all"}
                    </Button>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    mb: 1,
                    px: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      width: 110,
                      height: 10,
                      borderRadius: 999,
                      background:
                        "linear-gradient(90deg, rgba(79, 70, 229, 0.06), rgba(79, 70, 229, 0.94))",
                      border: `1px solid ${t.colors.borderLight}`,
                    }}
                  />
                  <Typography sx={{ fontSize: "0.7rem", color: t.colors.textMuted, fontFamily: t.fontFamily }}>
                    Low
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", color: t.colors.textMuted, fontFamily: t.fontFamily }}>
                    High
                  </Typography>
                </Box>

                <Box
                  sx={{
                    maxHeight: 260,
                    overflowY: "auto",
                    overflowX: "hidden",
                    borderRadius: t.radius.md,
                    border: `1px solid ${t.colors.border}`,
                    backgroundColor: t.colors.card,
                  }}
                >
                  {/* Sticky header to keep context while scrolling */}
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      display: "grid",
                      gridTemplateColumns: servicesGridTemplateColumns,
                      columnGap: "6px",
                      backgroundColor: t.colors.borderLight,
                      borderBottom: `1px solid ${t.colors.border}`,
                      px: 1,
                      py: 1,
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        fontFamily: t.fontFamily,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: t.colors.textMuted,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Service
                    </Box>
                    {visibleServiceColumns.map((col) => (
                      <Box
                        key={col.id}
                        sx={{
                          fontFamily: t.fontFamily,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: t.colors.textMuted,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          textAlign: "right",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col.label}
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ px: 1, py: 0.5 }}>
                    {displayedServiceRows.map((row) => (
                      <Box
                        key={row.key}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: servicesGridTemplateColumns,
                          columnGap: "6px",
                          alignItems: "center",
                          py: 0.5,
                        }}
                      >
                        {/* Unshaded service name */}
                        <Box
                          sx={{
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            fontFamily: t.fontFamily,
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                            color: t.colors.text,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            pr: 1,
                          }}
                        >
                          <Box sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                            {row.service}
                          </Box>
                          <Box
                            title={`Impact score: ${Math.round((row.riskScore ?? 0) * 100)}%`}
                            sx={{
                              ml: 1,
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor: getHeatColor(row.riskScore ?? 0),
                              border: `1px solid ${t.colors.borderLight}`,
                              flexShrink: 0,
                            }}
                          />
                        </Box>

                        {visibleServiceColumns.map((col) => {
                          const cell = row.cells[col.id];
                          const value = cell?.value;
                          const norm = cell?.norm;
                          const bg = getHeatColor(norm);
                          const isHot = norm != null && norm > 0.75;

                          const displayValue =
                            col.id === "cost_unblended_mtd_total" &&
                            value != null
                              ? new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }).format(value)
                              : value == null
                              ? "—"
                              : value;

                          return (
                            <Tooltip
                              key={col.id}
                              title={
                                value == null
                                  ? "No data"
                                  : `${col.label}: ${displayValue}`
                              }
                              placement="top"
                              arrow
                            >
                              <Box
                                sx={{
                                  height: 28,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  backgroundColor: bg,
                                  borderRadius: "6px",
                                  px: 1.25,
                                  fontSize: "0.75rem",
                                  border: `1px solid ${
                                    isHot ? "rgba(79, 70, 229, 0.35)" : t.colors.borderLight
                                  }`,
                                  color: norm != null && norm > 0.65 ? "#fff" : t.colors.text,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  transition:
                                    "transform 120ms ease, box-shadow 120ms ease",
                                  "&:hover": {
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.12)",
                                  },
                                }}
                              >
                                {displayValue}
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Bottom half: Account details */}
      <Box
        sx={{
          borderRadius: t.radius.lg,
          backgroundColor: t.colors.card,
          border: `1px solid ${t.colors.border}`,
          boxShadow: t.shadow,
          p: 2,
        }}
      >
        <SummaryAccountDetailContent
          accountNumber={selectedAccountNumber}
          showBackLink={false}
          severity={severityFilter}
          onSeverityChange={setSeverityFilter}
          framework={frameworkFilter}
          onFrameworkChange={setFrameworkFilter}
          frameworks={Array.isArray(overall?.compliance_by_framework) ? overall.compliance_by_framework : []}
        />
      </Box>
    </Box>
  );
};

export default SummaryPage;
